import * as React from 'react'
import { ReactNode } from 'react'
import clx from 'classnames'
import { Link } from 'react-router-dom'
type ButtonStatus = 'fill' | 'outlined'
type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral'
  | 'tertiary'
  | 'link'
  | 'accent'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

const variantColorClasses: Record<
  ButtonStatus,
  Record<ButtonVariant | 'disabled' | 'disabledGhost', string>
> = {
  fill: {
    primary: 'tw-d-btn tw-d-btn-primary',
    secondary: 'tw-d-btn tw-d-btn-secondary',
    success: 'tw-d-btn tw-d-btn-success',
    warning: 'tw-d-btn tw-d-btn-warning',
    error: 'tw-d-btn tw-d-btn-error',
    neutral: 'tw-d-btn tw-d-btn-neutral',
    accent: 'tw-d-btn tw-d-btn-accent',
    tertiary: 'tw-d-btn tw-d-btn-ghost tw-text-base-content',
    link: 'tw-d-btn tw-underline tw-underline-offset-4 !tw-text-primary !tw-border-none tw-shadow-none !tw-font-normal',
    disabled:
      'tw-d-btn !tw-border-none !tw-bg-content-base/20 !tw-text-[#182939]/20',
    disabledGhost: 'tw-opacity-20',
  },
  outlined: {
    primary: 'tw-d-btn tw-d-btn-outline tw-d-btn-primary',
    secondary: 'tw-d-btn tw-d-btn-outline tw-d-btn-secondary',
    success: 'tw-d-btn tw-d-btn-outline tw-d-btn-success',
    warning: 'tw-d-btn tw-d-btn-outline tw-d-btn-warning',
    error: 'tw-d-btn tw-d-btn-outline tw-d-btn-error',
    neutral: 'tw-d-btn tw-d-btn-outline tw-d-btn-neutral',
    accent: 'tw-d-btn tw-d-btn-outline tw-d-btn-accent',
    tertiary: 'tw-d-btn tw-d-btn-ghost tw-text-base-content',
    link: 'tw-d-btn tw-underline tw-underline-offset-4 !tw-text-primary !tw-border-none tw-shadow-none !tw-font-normal',
    disabled:
      'tw-d-btn tw-d-btn-outline !tw-text-[#182939] !tw-bg-transparent tw-opacity-20',
    disabledGhost: 'tw-opacity-20',
  },
}

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'tw-d-btn-xs',
  sm: 'tw-d-btn-sm',
  md: 'tw-d-btn-md',
  lg: 'tw-d-btn-lg',
}

type HTMLButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>
type HTMLAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>

type ButtonProps = {
  children?: ReactNode
  variant?: ButtonVariant
  status?: ButtonStatus
  size?: ButtonSize
  text?: string
}

type Props =
  | ({ as?: 'button' } & HTMLButtonProps & ButtonProps)
  | ({ as?: 'a' } & HTMLAnchorProps & ButtonProps)

export const Button = ({
  as = 'button',
  children,
  size = 'md',
  status = 'fill',
  variant = 'primary',
  className,
  text,
  ...rest
}: Props) => {
  const isDisabled = 'disabled' in rest && rest.disabled

  const sizeClass = sizeClasses[size]
  const useDisabledGhost =
    isDisabled && (variant === 'tertiary' || variant === 'link')

  const variantClass =
    variantColorClasses[status][
      useDisabledGhost ? 'disabledGhost' : isDisabled ? 'disabled' : variant
    ]
  const classNames = clx(variantClass, sizeClass, className)

  if (as === 'a' || ('href' in rest && typeof rest.href === 'string')) {
    return (
      <Link
        to={(rest as HTMLAnchorProps).href || '/#'}
        className={clx('tw-gap-2 tw-rounded-2xl', classNames)}
        {...(rest as HTMLAnchorProps)}
      >
        {children || text ? (
          <>
            {children}
            {text}
          </>
        ) : null}
      </Link>
    )
  }

  return (
    <button
      className={clx('tw-d-btn tw-gap-2 tw-rounded-2xl', classNames)}
      {...(rest as ButtonProps)}
    >
      {children || text ? (
        <>
          {children}
          {text}
        </>
      ) : null}
    </button>
  )
}
